import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../apiConfig";
import { useParams } from "react-router-dom";
import { BarChart3, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function PlalyerStat() {
    const playerId = useParams();
    const [battingData, setBattingData] = useState([]);
    const [bowlingData, setBowlingData] = useState([]);
    const [statView, setStatView] = useState('batting'); // 'batting' or 'bowling'
    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        axios.get(`${API_BASE_URL}/playerStats/${playerId.id}`).then((res) => {
            if (res.data) {
                setBattingData(res.data.batstats || []);
                setBowlingData(res.data.bowstats || []);
            }
        });
    }, [playerId.id]);

    const FONT_DISPLAY = "'Barlow Condensed', sans-serif";
    const FONT_BODY = "'Rajdhani', sans-serif";
    const accentYellow = '#E7C424';

    const styles = {
        container: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
        },
        headerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
        },
        titleBox: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        title: {
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: isMobile ? '28px' : '36px',
            color: '#0A1172',
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        selectWrapper: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
        },
        select: {
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: '16px',
            padding: '10px 40px 10px 20px',
            border: '2px solid #0A1172',
            borderRadius: '4px',
            background: '#FFFFFF',
            cursor: 'pointer',
            appearance: 'none',
            color: '#0A1172',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            transition: 'all 0.3s',
            outline: 'none',
        },
        tableWrapper: {
            width: '100%',
            background: 'linear-gradient(180deg, #0A1172 0%, #040960 100%)',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            textAlign: 'center',
            minWidth: isMobile ? '600px' : 'auto',
        },
        th: {
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: isMobile ? '14px' : '17px',
            color: 'rgba(255,255,255,0.6)',
            padding: isMobile ? '12px 8px' : '20px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.15)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            background: 'rgba(0,0,0,0.2)',
        },
        td: {
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: isMobile ? '15px' : '18px',
            color: '#FFFFFF',
            padding: isMobile ? '14px 8px' : '18px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
        }
    };

    const renderTable = () => {
        const isBatting = statView === 'batting';
        const data = isBatting ? battingData : bowlingData;

        return (
            <AnimatePresence mode="wait">
                <motion.div
                    key={statView}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: '100%', overflowX: isMobile ? 'auto' : 'visible' }}
                >
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Season</th>
                                <th style={styles.th}>Matches</th>
                                {isBatting ? (
                                    <>
                                        <th style={styles.th}>Runs</th>
                                        <th style={styles.th}>50s</th>
                                        <th style={styles.th}>100s</th>
                                        <th style={styles.th}>High Score</th>
                                        <th style={{...styles.th, color: accentYellow}}>Avg</th>
                                    </>
                                ) : (
                                    <>
                                        <th style={styles.th}>Wickets</th>
                                        <th style={styles.th}>ER</th>
                                        <th style={styles.th}>BB</th>
                                        <th style={{...styles.th, color: accentYellow}}>Avg</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? data.map((item, id) => (
                                <motion.tr 
                                    key={id}
                                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                                    style={{ background: id % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}
                                >
                                    <td style={{...styles.td, fontFamily: FONT_DISPLAY, color: accentYellow}}>{item.year}</td>
                                    <td style={styles.td}>{item.matchs}</td>
                                    {isBatting ? (
                                        <>
                                            <td style={styles.td}>{item.runs}</td>
                                            <td style={styles.td}>{item['50s']}</td>
                                            <td style={styles.td}>{item['100s']}</td>
                                            <td style={styles.td}>{item.BS}</td>
                                            <td style={{...styles.td, color: accentYellow, fontWeight: 900}}>{item.AVG}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={styles.td}>{item.wkts}</td>
                                            <td style={styles.td}>{item.ER}</td>
                                            <td style={styles.td}>{item.BB}</td>
                                            <td style={{...styles.td, color: accentYellow, fontWeight: 900}}>{item.AVG}</td>
                                        </>
                                    )}
                                </motion.tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" style={{ ...styles.td, padding: '60px', opacity: 0.4 }}>
                                        No performance data available for this category.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </motion.div>
            </AnimatePresence>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerRow}>
                <div style={styles.titleBox}>
                    <BarChart3 color="#0A1172" size={28} />
                    <h2 style={styles.title}>Career Stats</h2>
                </div>
                <div style={styles.selectWrapper}>
                    <select 
                        style={styles.select} 
                        value={statView} 
                        onChange={(e) => setStatView(e.target.value)}
                    >
                        <option value="batting">Batting Statistics</option>
                        <option value="bowling">Bowling Statistics</option>
                    </select>
                    <ChevronDown size={18} style={{ position: 'absolute', right: '12px', pointerEvents: 'none', color: '#0A1172' }} />
                </div>
            </div>

            <div style={styles.tableWrapper}>
                {renderTable()}
            </div>
        </div>
    );
}

export default PlalyerStat;